import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { AppDownloadModal } from './AppDownloadModal';
import { IconBrandApple, IconBrandGooglePlay } from '@tabler/icons-react';

type Platform = 'ios' | 'android' | 'macos' | 'desktop';

interface AppStoreButtonsProps {
  appStoreUrl?: string;
  googlePlayUrl?: string;
  siteName?: string;
  className?: string;
}

function detectPlatform(): Platform {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

  // iOS detection
  if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
    return 'ios';
  }

  // macOS detection
  if (/Macintosh|MacIntel|MacPPC|Mac68K/.test(navigator.platform)) {
    return 'macos';
  }

  // Android detection
  if (/android/i.test(userAgent)) {
    return 'android';
  }

  // Default to desktop for Windows, Linux, etc.
  return 'desktop';
}

export function AppStoreButtons({
  appStoreUrl,
  googlePlayUrl,
  siteName = 'our app',
  className = '',
}: AppStoreButtonsProps) {
  const [appStoreModalOpen, setAppStoreModalOpen] = useState(false);
  const [googlePlayModalOpen, setGooglePlayModalOpen] = useState(false);

  const handleAppStoreClick = () => {
    if (!appStoreUrl) return;

    const platform = detectPlatform();

    switch (platform) {
      case 'ios':
        window.open(appStoreUrl, '_blank');
        break;
      default:
        // Desktop/macOS/Android - show QR code modal
        setAppStoreModalOpen(true);
        break;
    }
  };

  const handleGooglePlayClick = () => {
    if (!googlePlayUrl) return;

    const platform = detectPlatform();

    switch (platform) {
      case 'android':
        window.open(googlePlayUrl, '_blank');
        break;
      default:
        // Desktop/macOS/iOS - show QR code modal
        setGooglePlayModalOpen(true);
        break;
    }
  };

  if (!appStoreUrl && !googlePlayUrl) {
    return null;
  }

  return (
    <>
      <div className={`flex gap-2 ${className}`}>
        {appStoreUrl && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleAppStoreClick}
            className="bg-transparent border-neutral-50/50 text-neutral-50 hover:bg-neutral-50/10 hover:border-neutral-50 hover:text-neutral-50"
          >
            <IconBrandApple className="h-4 w-4 mr-2" />
            App Store
          </Button>
        )}
        {googlePlayUrl && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleGooglePlayClick}
            className="bg-transparent border-neutral-50/50 text-neutral-50 hover:bg-neutral-50/10 hover:border-neutral-50 hover:text-neutral-50"
          >
            <IconBrandGooglePlay className="h-4 w-4 mr-2" />
            Google Play
          </Button>
        )}
      </div>

      <AppDownloadModal
        open={appStoreModalOpen}
        onOpenChange={setAppStoreModalOpen}
        storeUrl={appStoreUrl}
        storeType="appstore"
        siteName={siteName}
      />

      <AppDownloadModal
        open={googlePlayModalOpen}
        onOpenChange={setGooglePlayModalOpen}
        storeUrl={googlePlayUrl}
        storeType="googleplay"
        siteName={siteName}
      />
    </>
  );
}
